'use client'

import cn from 'classnames'
import { useTranslations } from 'next-intl'
import { useParams } from 'next/navigation'

import { useBookingExternal } from '@booking/state/external.context'
import { formatMainLink, type Locale } from '@/src/lib/link'
import styles from './index.module.scss'

// ---------------------------------------------------------------------------

type ProtectChoice = 'yes' | 'no'

type ProtectSectionProps = {
  choice: ProtectChoice | null
  onChoose: (choice: ProtectChoice) => void
  showError: boolean
}

export default function ProtectSection({ choice, onChoose, showError }: ProtectSectionProps) {
  const t = useTranslations('booking')
  const { externalData } = useBookingExternal()
  //const params = useParams<{ locale?: string }>()
  //const locale = (params?.locale ?? 'pt') as Locale

  const termsHref = formatMainLink(externalData?.termsPath ?? 'terms')
  const pending = choice === null

  return (
    <section className={styles.section}>
      <h2 className={styles.title}>
        {t('ticket.payment.protect.title')}
        {pending && <span className={styles.hint}> — {t('ticket.payment.protect.hint')}</span>}
      </h2>

      {/* Single bordered card containing description + options */}
      <div className={styles.infoCard}>
        <div className={styles.infoTop}>
          <div className={styles.infoIcon}>
            <ShieldSvg size={22} color="var(--main-purple)" />
          </div>
          <div className={styles.infoBody}>
            <strong className={styles.infoTitle}>{t('ticket.payment.protect.infoTitle')}</strong>
            <p className={styles.infoDesc}>
              {t.rich('ticket.payment.protect.infoDesc', {
                terms: (chunks) => (
                  <a
                    href={termsHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.link}
                  >
                    {chunks}
                  </a>
                ),
              })}
            </p>
          </div>
        </div>

        {/* Yes / No option cards — inside the same bordered box */}
        <div className={styles.options}>
          <button
            type="button"
            aria-pressed={choice === 'yes'}
            className={cn(
              styles.optionCard,
              styles.optionYes,
              choice === 'yes' && styles.optionYesSelected,
            )}
            onClick={() => onChoose('yes')}
          >
            {choice !== 'yes' && (
              <span className={styles.recommended}>{t('ticket.payment.protect.recommended')}</span>
            )}
            {choice === 'yes' && <CheckBadge className={styles.checkBadge} />}
            <ShieldSvg
              size={28}
              color={choice === 'yes' ? '#fff' : 'var(--main-purple)'}
              className={styles.optionIcon}
            />
            <strong className={styles.optionTitle}>{t('ticket.payment.protect.yesTitle')}</strong>
            <span className={styles.optionSub}>{t('ticket.payment.protect.yesPrice')}</span>
          </button>

          <button
            type="button"
            aria-pressed={choice === 'no'}
            className={cn(
              styles.optionCard,
              styles.optionNo,
              choice === 'no' && styles.optionNoSelected,
            )}
            onClick={() => onChoose('no')}
          >
            {choice === 'no' && <CheckBadge className={styles.checkBadge} />}
            <span className={cn(styles.xIcon, choice === 'no' && styles.xIconSelected)}>×</span>
            <strong className={styles.optionTitle}>{t('ticket.payment.protect.noTitle')}</strong>
            <span className={styles.optionSub}>{t('ticket.payment.protect.noSub')}</span>
          </button>
        </div>
        {/* end .options */}

        {choice === null && (
          <p className={styles.error}>
            <InfoIcon />
            {t('ticket.payment.protect.error')}
          </p>
        )}
      </div>
      {/* end .infoCard */}
    </section>
  )
}

// ---------------------------------------------------------------------------
// Inline SVG helpers

function ShieldSvg({
  size,
  color,
  className,
}: {
  size: number
  color: string
  className?: string
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M12 2L3 6v6c0 5.25 3.75 10.15 9 11.35C17.25 22.15 21 17.25 21 12V6L12 2z"
        stroke={color}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function CheckBadge({ className }: { className?: string }) {
  return (
    <span className={cn(styles.checkBadgeBase, className)}>
      <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M1 4L3.5 6.5L9 1"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  )
}

function InfoIcon() {
  return (
    <svg style={{ flexShrink: 0 }} width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
      <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm0 11.5a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5zm.75-3.75a.75.75 0 0 1-1.5 0v-3a.75.75 0 0 1 1.5 0v3z" />
    </svg>
  )
}
